/**
 * The refusal model, once, for the whole application flow.
 *
 * Every refusal on this platform is RFC 9457 `application/problem+json` with required members
 * {type, title, status, error_code}. `error_code` is THE contract; `title` and `detail` are
 * explicitly NOT places a client branches (authors-api.yaml, shared.yaml `Problem`). So this file
 * gives the rest of the feature exactly one way to ask "which refusal was that" — a string
 * comparison against a generated union — and no way to branch on prose.
 *
 * WHY A CLASS AND NOT A RESULT TYPE. Every call site here is inside an async React handler that
 * already has a try/catch for the network failure it cannot avoid. A second, parallel error
 * channel would mean two shapes to remember at every call site.
 */
import type { TFunction } from 'i18next';
import { i18n } from '../../../i18n';
import type { ErrorCode, Problem } from '../contract';
import { MAX_UPLOAD_BYTES } from '../domain/limits';
// The app's one `Retry-After` reader and one 429 sentence, shared with the cabinet's screens.
import { retryAfterOf, tooManyRequests } from '../../cabinet/api/problem';

export { retryAfterOf, tooManyRequests };

/**
 * The fourteen codes this feature can actually receive, derived from the operations it calls.
 *
 * The `satisfies` clause is the point, not the array: it is a COMPILE-TIME CHECK that every code
 * this feature branches on is one of the 65 registered in the generated `ErrorCode` union. Invent
 * a code, or mistype one, and the build stops here rather than at a branch that never fires.
 */
export const APPLICATION_ERROR_CODES = [
  'TERMS_ACCEPTANCE_STALE',
  'APPLICATION_INCOMPLETE',
  'APPLICATION_ALREADY_OPEN',
  'SLUG_TAKEN',
  'SLUG_FROZEN',
  'RESERVED_NAME',
  'PROFILE_LIMIT_EXCEEDED',
  'UPLOAD_OBJECT_NOT_FOUND',
  'UPLOAD_TYPE_REJECTED',
  'UPLOAD_DIMENSIONS_REJECTED',
  'UPLOAD_TOO_LARGE',
  'UPLOAD_TOO_SMALL',
  'AUTHOR_CAPABILITY_REQUIRED',
  // G30 on the two submits (design 22 §1.4) - the generic word, the one uncoded 422 they declare.
  'UNPROCESSABLE_UNSPECIFIED',
] as const satisfies readonly ErrorCode[];

/**
 * A refusal the server explained. `problem` is present when the body parsed as RFC 9457; it is
 * absent for a transport failure, which is a different thing and must be worded differently.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | undefined;
  readonly errorCode: ErrorCode | undefined;
  /** `Retry-After` in seconds, when the answer carried one `retryAfterOf` could read. */
  readonly retryAfterSeconds: number | undefined;

  constructor(
    status: number,
    problem: Problem | undefined,
    message: string,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.errorCode = problem?.error_code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** The request never reached an answer: DNS, offline, CORS, an aborted fetch. Not a refusal. */
export class TransportError extends Error {
  override readonly cause: unknown;

  constructor(message: string, cause: unknown) {
    super(message);
    this.name = 'TransportError';
    this.cause = cause;
  }
}

/**
 * Firebase Storage answered leg one of an upload - the WRITE into the quarantine prefix - and
 * refused it. Only the real writer raises it (`toUploadError`,
 * ../../storage/firebase-quarantine-uploader.ts), so it can never be an answer from our API.
 *
 * AN {@link ApiError} WITH STORAGE'S OWN STATUS AND NO PROBLEM, because Storage does not answer in
 * RFC 9457: everything that words a status - a 401 is "sign in again" - still does. It is a class of
 * its own for the one status that means something only here: a 403 is the quarantine rules saying
 * no (docs/design/adr/0010-firebase-integration.md). Told apart by where it came from and
 * never by its shape, because a bare 403 from a proxy in front of our API has the same shape and
 * must not be worded as a refused file.
 */
export class StorageRefusal extends ApiError {
  override readonly name = 'StorageRefusal';
  /** The SDK's own code, `storage/` and a name. For a log line; a screen never shows it. */
  readonly storageCode: string;

  constructor(status: number, storageCode: string) {
    super(
      status,
      undefined,
      `Firebase Storage refused the upload (${storageCode}, ${String(status)}).`,
    );
    this.storageCode = storageCode;
  }
}

/**
 * A file refused BEFORE A BYTE MOVED: it is `MAX_UPLOAD_BYTES` or more (../domain/limits.ts says
 * where the ceiling is ruled and why it binds at `>=`). Raised by the two upload hooks
 * (../data/useUpload.ts) and by nothing else - neither leg of the upload was attempted.
 */
export class FileTooLarge extends Error {
  override readonly name = 'FileTooLarge';
  /** The file's own size. For a log line; the sentence names the ceiling instead. */
  readonly bytes: number;

  constructor(bytes: number) {
    super(
      `The file is ${String(bytes)} bytes, and an upload has to be under ${String(MAX_UPLOAD_BYTES)}.`,
    );
    this.bytes = bytes;
  }
}

/**
 * Leg one of an upload STOPPED MOVING: no byte of the write went out for the whole of the real
 * writer's stall window, so the writer cancelled it and said so (`UPLOAD_STALL_WINDOW_MS`,
 * ../../storage/firebase-quarantine-uploader.ts, says how long and why). Only that writer raises
 * it.
 *
 * A {@link TransportError}, because that is what it is - the write never reached an answer - so a
 * screen that words only a transport failure still says something true of it: check the
 * connection. A class of its own so that {@link humanMessage} can say what HAPPENED - the upload
 * stopped, and was stopped for the person - and so that it is never read as the person's own
 * cancel, which is an `AbortError` and is worded as nothing at all.
 */
export class UploadStalled extends TransportError {
  override readonly name = 'UploadStalled';
  /** How long nothing moved. For a log line, like the byte counts; a screen shows none of them. */
  readonly windowMs: number;
  readonly bytesTransferred: number;
  readonly totalBytes: number;

  constructor(windowMs: number, bytesTransferred: number, totalBytes: number) {
    super(
      `No byte of the upload moved for ${String(windowMs)} ms (${String(bytesTransferred)} of ${String(totalBytes)} sent), so it was cancelled.`,
      undefined,
    );
    this.windowMs = windowMs;
    this.bytesTransferred = bytesTransferred;
    this.totalBytes = totalBytes;
  }
}

/** True when `value` is an {@link ApiError} carrying exactly this registered machine code. */
export function isErrorCode(value: unknown, code: ErrorCode): boolean {
  return value instanceof ApiError && value.errorCode === code;
}

/** True when `value` is an {@link ApiError} with this HTTP status. */
export function isStatus(value: unknown, status: number): boolean {
  return value instanceof ApiError && value.status === status;
}

/**
 * 503 is the ONE case where the app HOLDS THE SESSION AND RETRIES rather than signing the person
 * out (09-authors-app.md). It means Google could not be reached, not that the caller is
 * not who they said they were, and treating it as a 401 signs a working author out of their own
 * cabinet for somebody else's outage.
 */
export function isUpstreamUnavailable(value: unknown): boolean {
  return isStatus(value, 503);
}

/** 401. The session is gone and only signing in again fixes it. */
export function isSessionGone(value: unknown): boolean {
  return isStatus(value, 401);
}

/**
 * 429. authors-api's twenty 429 responses all declare `Retry-After` since decision-log row 220
 * (the gap 09-authors-app.md recorded). ./http.ts reads it onto
 * {@link ApiError.retryAfterSeconds}, and {@link humanMessage} says the wait.
 */
export function isRateLimited(value: unknown): boolean {
  return isStatus(value, 429);
}

/**
 * The fixed schedule, in milliseconds, for a 429 that named no wait. It is used by the one thing on
 * this surface that polls on its own — the public-address check, which fires as somebody types and
 * waits the 429's own `Retry-After` instead when it carried one (../data/useSlugCheck.ts).
 * Everything else here is a button press, and a person pressing a button again IS the back-off.
 */
export const RATE_LIMIT_BACKOFF_MS = [1_000, 4_000, 10_000] as const;

/** The delay to wait after `attempt` consecutive 429s (0-based). Clamps at the last step. */
export function backoffFor(attempt: number): number {
  const index = Math.min(Math.max(attempt, 0), RATE_LIMIT_BACKOFF_MS.length - 1);
  return RATE_LIMIT_BACKOFF_MS[index] ?? RATE_LIMIT_BACKOFF_MS[0];
}

/**
 * WHICH OF THE TWO UPLOADS A REFUSAL IS ABOUT. One refusal can need two sentences, because the two
 * claims accept different things: the photograph is a JPEG, PNG or WebP and nothing else
 * (`avatar-claim.service.ts` narrows to `image/`), while identity evidence is any row of
 * `ref_content_type.is_accepted_upload` (`credential.service.ts`) - four rows, a PDF as
 * well as the three images (prisma/migrations/0003_lookups_and_seeds/migration.sql).
 * Both claims tell the type by MAGIC BYTES, never by the file's name or its declared type.
 */
export type UploadSubject = 'avatar' | 'credential';

/** `20`: the ceiling as a person reads it. MB here is the rule's own 1024 * 1024. */
const CEILING_MB = String(MAX_UPLOAD_BYTES / (1024 * 1024));

/**
 * The sentences that differ by {@link UploadSubject}, as `application` catalogue keys. Everything
 * else is worded once, below.
 *   tooLargeToWrite  {@link FileTooLarge}: refused here, before a byte moved.
 *   storageRefused   a {@link StorageRefusal} with 403: the rules admit only an image or a PDF
 *                    under the ceiling.
 *   typeRejected     `UPLOAD_TYPE_REJECTED`: the bytes are none of the types this claim accepts.
 *   unreadable       `UPLOAD_DIMENSIONS_REJECTED`: an image whose own header could not be read.
 *                    Only an IMAGE reaches it for evidence - a PDF's dimensions are never read
 *                    (`credential.service.ts`) - so a PDF is the other way out.
 *   tooSmall         `UPLOAD_TOO_SMALL`, or `null` where the claim has no floor and never sends it.
 *                    The credential's `file_kind` row has none
 *                    (prisma/migrations/0003_lookups_and_seeds/migration.sql), and its claim never
 *                    sends this code (`credential.service.ts`). Were it ever to, the panel's own
 *                    sentence is honest and the photograph's 256-pixel floor would not be.
 */
const UPLOAD_SENTENCES = {
  avatar: {
    tooLargeToWrite: 'problem.upload.avatar.tooLargeToWrite',
    storageRefused: 'problem.upload.avatar.storageRefused',
    typeRejected: 'problem.upload.avatar.typeRejected',
    unreadable: 'problem.upload.avatar.unreadable',
    tooSmall: 'problem.upload.avatar.tooSmall',
  },
  credential: {
    tooLargeToWrite: 'problem.upload.credential.tooLargeToWrite',
    storageRefused: 'problem.upload.credential.storageRefused',
    typeRejected: 'problem.upload.credential.typeRejected',
    unreadable: 'problem.upload.credential.unreadable',
    tooSmall: null,
  },
} as const satisfies Readonly<Record<UploadSubject, Readonly<Record<string, string | null>>>>;

/**
 * What to SHOW a person. Never the machine code and never `detail` verbatim: `detail` is scoped
 * for a human reading a log, and a code on screen is noise to everyone who is not us.
 *
 * The one honest use of the server's own `title` is the case we did not anticipate — a code this
 * feature does not know about. There we fall back to a plain sentence rather than to the code.
 *
 * `subject` NAMES THE UPLOAD a refusal came from; see {@link UploadSubject}. The evidence panel
 * passes `'credential'`. Omitted, an upload's refusal is the PHOTOGRAPH's: `AvatarField` asks in
 * the two-argument form, and no other screen can be handed an upload's refusal at all - the
 * `UPLOAD_*` codes come only from the two claims, and the three upload classes above only from the
 * uploads.
 */
export function humanMessage(
  value: unknown,
  fallback: string,
  subject?: UploadSubject,
  // A screen passes its own `t`; anything else reads the catalogue in the language now in force.
  t: TFunction<'application'> = i18n.getFixedT(null, 'application'),
): string {
  // Before the transport sentence, whose class it extends. Worded once: a stall says nothing about
  // what the file is, so the photograph and the evidence read the same words.
  if (value instanceof UploadStalled) {
    return t('problem.uploadStalled');
  }
  if (value instanceof TransportError) {
    return t('problem.transport');
  }
  const upload = UPLOAD_SENTENCES[subject ?? 'avatar'];
  if (value instanceof FileTooLarge) return t(upload.tooLargeToWrite, { mb: CEILING_MB });
  if (!(value instanceof ApiError)) return fallback;

  switch (value.errorCode) {
    case 'APPLICATION_INCOMPLETE':
      return t('problem.code.applicationIncomplete');
    case 'TERMS_ACCEPTANCE_STALE':
      return t('problem.code.termsAcceptanceStale');
    case 'APPLICATION_ALREADY_OPEN':
      return t('problem.code.applicationAlreadyOpen');
    case 'SLUG_FROZEN':
      return t('problem.code.slugFrozen');
    case 'SLUG_TAKEN':
      return t('problem.code.slugTaken');
    case 'RESERVED_NAME':
      return t('problem.code.reservedName');
    case 'PROFILE_LIMIT_EXCEEDED':
      return t('problem.code.profileLimitExceeded');
    case 'UPLOAD_OBJECT_NOT_FOUND':
      return t('problem.code.uploadObjectNotFound');
    case 'UPLOAD_TYPE_REJECTED':
      return t(upload.typeRejected);
    // One fault per code since ruling 10(a) split them - `UPLOAD_TOO_LARGE` over the byte ceiling,
    // `UPLOAD_TOO_SMALL` under the pixel floor, `UPLOAD_DIMENSIONS_REJECTED` for an image whose
    // size cannot be read at all (authors-api.yaml). The numbers are the seeded rows of
    // `file_kind` (prisma/migrations/0003_lookups_and_seeds/migration.sql: 20 MB for
    // both kinds, 256 by 256 pixels for an avatar, no floor for a credential), and the same 20 MB
    // is the quarantine rule's own ceiling (adr/0010-firebase-integration.md).
    case 'UPLOAD_DIMENSIONS_REJECTED':
      return t(upload.unreadable);
    case 'UPLOAD_TOO_LARGE':
      return t('problem.code.uploadTooLarge', { mb: CEILING_MB });
    case 'UPLOAD_TOO_SMALL':
      return upload.tooSmall === null ? fallback : t(upload.tooSmall);
    case 'AUTHOR_CAPABILITY_REQUIRED':
      return t('problem.code.authorCapabilityRequired');
    default:
      break;
  }

  // Storage's 403 carries no code to branch on, and the class says which leg it came from.
  if (value instanceof StorageRefusal && value.status === 403) {
    return t(upload.storageRefused, { mb: CEILING_MB });
  }
  if (value.status === 401) return t('problem.status.sessionEnded');
  if (value.status === 429)
    return tooManyRequests(value.retryAfterSeconds, t('problem.status.tooManyRequests'));
  if (value.status === 503) return t('problem.status.unavailable');
  return fallback;
}

/**
 * Read a Problem out of a response body. Deliberately forgiving: a proxy or an edge failure can
 * answer a non-JSON 502 that never came from our process at all, and turning that into a parse
 * exception buries the real status.
 */
export async function readProblem(response: Response): Promise<Problem | undefined> {
  try {
    const body: unknown = await response.json();
    if (
      typeof body === 'object' &&
      body !== null &&
      'error_code' in body &&
      typeof (body as { error_code: unknown }).error_code === 'string'
    ) {
      return body as Problem;
    }
  } catch {
    // Not JSON, or an empty body. Both are answered by `undefined`.
  }
  return undefined;
}
